import React, { useState, useRef } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Patient, Encounter } from '../types';
import { RadioIcon, UploadIcon, CheckCircleIcon, XIcon } from 'lucide-react';
export const RadiologyDashboard: React.FC = () => {
  const {
    patients,
    updateEncounter,
    addNotification
  } = useData();
  const {
    user
  } = useAuth();
  const [selectedEncounter, setSelectedEncounter] = useState<{
    patient: Patient;
    encounter: Encounter;
  } | null>(null);
  const [report, setReport] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const radQueue = patients.flatMap(p => p.encounters.filter(e => {
    if (e.status === 'Radiology Ordered') return true;
    // Also show encounters where clinician ordered scans but kept file open
    if (e.status === 'With Clinician' || e.status === 'Lab Ordered') {
      const hasRadOrders = e.investigations.ordered.scanUltrasound || e.investigations.ordered.cxr;
      const hasNoImages = !e.investigations.radiologyImages || e.investigations.radiologyImages.length === 0;
      return hasRadOrders && hasNoImages;
    }
    return false;
  }).map(e => ({
    patient: p,
    encounter: e
  })));
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };
  const handleSaveAndSend = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const updatedEncounter: Encounter = {
      ...encounter,
      investigations: {
        ...encounter.investigations,
        radiologyImages: [...encounter.investigations.radiologyImages, ...uploadedImages],
        radiologyReport: report,
        radiographerName: user?.name
      },
      status: 'With Clinician'
    };
    updateEncounter(patient.id, updatedEncounter);
    addNotification({
      targetRole: 'Clinician',
      message: `Scans and report uploaded for ${patient.personalDetails.fullNames}`,
      patientId: patient.id
    });
    setSelectedEncounter(null);
    setReport('');
    setUploadedImages([]);
  };
  const openEncounter = (item: {
    patient: Patient;
    encounter: Encounter;
  }) => {
    setSelectedEncounter(item);
    setReport(item.encounter.investigations.radiologyReport || '');
    setUploadedImages([]);
  };
  if (selectedEncounter) {
    const {
      patient,
      encounter
    } = selectedEncounter;
    return <div className="bg-white shadow rounded-lg border border-gray-100 p-6 max-w-3xl mx-auto" data-id="element-939">
        <div className="flex justify-between items-center mb-6 border-b pb-4" data-id="element-940">
          <div data-id="element-941">
            <h2 className="text-xl font-bold text-gray-800" data-id="element-942">
              Radiology Upload & Report
            </h2>
            <p className="text-sm text-gray-500" data-id="element-943">
              Patient: {patient.personalDetails.fullNames} | File:{' '}
              {patient.personalDetails.fileNo}
            </p>
          </div>
          <button onClick={() => setSelectedEncounter(null)} className="text-gray-500 hover:text-gray-700" data-id="element-944">
            Cancel
          </button>
        </div>

        <div className="mb-6 bg-purple-50 p-4 rounded-md border border-purple-100" data-id="element-945">
          <h3 className="text-sm font-medium text-purple-800 mb-1" data-id="element-946">
            Requested Scan:
          </h3>
          <p className="text-sm text-purple-900 font-semibold" data-id="element-947">
            {encounter.investigations.scanType || 'General Ultrasound / X-Ray'}
          </p>
        </div>

        <div className="space-y-6" data-id="element-948">
          <div data-id="element-949">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-950">
              Upload Scan Images
            </label>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-id="element-951" />
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 hover:border-purple-300 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-952">
              <UploadIcon className="mx-auto h-10 w-10 text-gray-400 mb-2" data-id="element-953" />
              <p className="text-sm text-gray-600 font-medium" data-id="element-954">
                Click to browse and upload images
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-955">
                PNG, JPG, DICOM up to 50MB
              </p>
            </div>

            {uploadedImages.length > 0 && <div className="mt-4" data-id="element-956">
                <p className="text-xs text-gray-500 mb-2" data-id="element-957">
                  {uploadedImages.length} image(s) selected
                </p>
                <div className="flex gap-3 overflow-x-auto pb-2" data-id="element-958">
                  {uploadedImages.map((img, idx) => <div key={idx} className="relative flex-shrink-0" data-id="element-959">
                      <img src={img} alt={`Scan ${idx + 1}`} className="h-28 w-28 object-cover rounded-md border border-gray-200" data-id="element-960" />
                      <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" data-id="element-961">
                        <XIcon className="h-3.5 w-3.5" data-id="element-962" />
                      </button>
                    </div>)}
                </div>
              </div>}
          </div>

          <div data-id="element-963">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-964">
              Radiographer's Report
            </label>
            <textarea rows={6} value={report} onChange={e => setReport(e.target.value)} placeholder="Enter detailed findings and conclusion..." className="w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-965" />
          </div>

          <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-sm" data-id="element-966">
            <p className="text-gray-600" data-id="element-967">
              Radiographer:{' '}
              <span className="font-semibold text-gray-800" data-id="element-968">{user?.name}</span>
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200" data-id="element-969">
            <button onClick={handleSaveAndSend} disabled={uploadedImages.length === 0 && !report.trim()} className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-970">
              <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" data-id="element-971" />
              Save & Send to Clinician
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-972">
      <h2 className="text-xl font-bold text-gray-800" data-id="element-973">Radiology Queue</h2>
      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100" data-id="element-974">
        <ul className="divide-y divide-gray-200" data-id="element-975">
          {radQueue.map(item => <li key={item.encounter.id} data-id="element-976">
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex items-center justify-between" data-id="element-977">
                <div className="flex items-center" data-id="element-978">
                  <div className="flex-shrink-0" data-id="element-979">
                    <RadioIcon className="h-8 w-8 text-purple-500" data-id="element-980" />
                  </div>
                  <div className="ml-4" data-id="element-981">
                    <p className="text-sm font-medium text-teal-600 truncate" data-id="element-982">
                      {item.patient.personalDetails.fullNames}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-983">
                      File No: {item.patient.personalDetails.fileNo}
                    </p>
                    {item.encounter.investigations.scanType && <p className="text-xs text-purple-600 mt-1" data-id="element-984">
                        Requested: {item.encounter.investigations.scanType}
                      </p>}
                  </div>
                </div>
                <button onClick={() => openEncounter(item)} className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700" data-id="element-985">
                  Upload Scans & Report
                </button>
              </div>
            </li>)}
          {radQueue.length === 0 && <li className="px-4 py-8 text-center text-gray-500" data-id="element-986">
              No pending radiology orders.
            </li>}
        </ul>
      </div>
    </div>;
};